package floris.graphics.backoffice.pages.admin

import floris.graphics.QueryOptions
import floris.graphics.SortCondition
import floris.graphics.models.domain.models.Category
import floris.graphics.models.domain.models.Contributor
import floris.graphics.models.domain.models.Techstack
import floris.graphics.models.domain.models.project.Project
import floris.graphics.models.domain.models.user.User
import floris.graphics.repo.CategoryRepo
import floris.graphics.repo.ContributorRepo
import floris.graphics.repo.ProjectRepo
import floris.graphics.repo.TechstackRepo
import floris.graphics.repo.UserRepo
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.async
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class AdminHomeViewModel(
    private val userRepo: UserRepo = UserRepo,
    private val projectRepo: ProjectRepo = ProjectRepo,
    private val contributorRepo: ContributorRepo = ContributorRepo,
    private val techStackRepo: TechstackRepo = TechstackRepo,
    private val categoryRepo: CategoryRepo = CategoryRepo,
    private val coroutineScope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
) {
    private val _state = MutableStateFlow(AdminHomeState())
    val state = _state.asStateFlow()

    private val _eventFlow = Channel<AdminHomeUiEvent>()
    val eventFlow = _eventFlow.receiveAsFlow()

    fun onEvent(event: AdminHomeEvent) = coroutineScope.launch {
        when (event) {
            is AdminHomeEvent.Navigate -> _eventFlow.send(AdminHomeUiEvent.Navigate(event.route))

            is AdminHomeEvent.LoadInitialData -> {
                _state.update { it.copy(isLoading = true) }

                val currentUserDeferred = async { userRepo.getCurrentUser() }
                val categoriesDeferred = async { categoryRepo.getCategories(QueryOptions(limit = 5, sorts = listOf(SortCondition(Category::updatedAt.name, false)))) }
                val techStacksDeferred = async { techStackRepo.getTechstacks(QueryOptions(limit = 5, sorts = listOf(SortCondition(Techstack::updatedAt.name, false)))) }
                val contributorsDeferred = async { contributorRepo.getContributors(QueryOptions(limit = 5, sorts = listOf(SortCondition(Contributor::updatedAt.name, false)))) }
                val projectsDeferred = async { projectRepo.getProjects(queryOptions = QueryOptions(limit = 5, sorts = listOf(SortCondition(Project::updatedAt.name, false)))) }

                val currentUser = currentUserDeferred.await()
                val categories = categoriesDeferred.await()
                val contributors = contributorsDeferred.await()
                val techStacks = techStacksDeferred.await()
                val projects = projectsDeferred.await()

                _state.update {
                    it.copy(
                        isLoading = false,
                        currentUser = currentUser,
                        categories = categories,
                        contributors = contributors,
                        techStacks = techStacks,
                        projects = projects
                    )
                }
            }
        }
    }

    init {
        onEvent(AdminHomeEvent.LoadInitialData)
    }
}

data class AdminHomeState(
    val isLoading: Boolean = false,
    val currentUser: User? = null,
    val projects: List<Project> = emptyList(),
    val contributors: List<Contributor> = emptyList(),
    val techStacks: List<Techstack> = emptyList(),
    val categories: List<Category> = emptyList(),
    val errorText: String = ""
)

sealed class AdminHomeEvent {
    data object LoadInitialData : AdminHomeEvent()
    data class Navigate(val route: String) : AdminHomeEvent()
}

sealed class AdminHomeUiEvent {
    data class Navigate(val route: String) : AdminHomeUiEvent()
}
